import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  ArrowLeft, 
  Save, 
  Upload, 
  ExternalLink,
  User,
  Briefcase,
  Phone,
  Mail,
  Globe,
  Linkedin,
  Instagram,
  MessageCircle,
  Youtube,
  Facebook,
  Twitter,
  Github,
  Hash,
  Palette,
  ImageIcon,
  X
} from 'lucide-react';
import ImageCropper from '../../components/ImageCropper';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProfileEditor() {
  const { profileId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = useAuth();
  
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [cropImage, setCropImage] = useState(null);
  const [cropType, setCropType] = useState(null);

  const BANNER_COLORS = [
    { value: '#1e293b', label: 'Anthracite' },
    { value: '#1e40af', label: 'Bleu' },
    { value: '#7c3aed', label: 'Violet' },
    { value: '#059669', label: 'Emeraude' },
    { value: '#dc2626', label: 'Rouge' },
    { value: '#ea580c', label: 'Orange' },
    { value: '#0891b2', label: 'Cyan' },
    { value: '#4f46e5', label: 'Indigo' },
  ];

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId]);

  const fetchProfile = async () => {
    try {
      const response = await fetch(`${API}/owner/profiles/${profileId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        
      });

      if (response.ok) {
        const data = await response.json();
        setProfile(data);
      } else {
        toast.error(t('errors.profileNotFound'));
        navigate('/owner');
      }
    } catch (error) {
      toast.error(t('errors.serverError'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const response = await fetch(`${API}/owner/profiles/${profileId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        
        body: JSON.stringify({
          first_name: profile.first_name,
          last_name: profile.last_name,
          job_title: profile.job_title,
          company: profile.company,
          bio: profile.bio,
          phone: profile.phone,
          office_phone: profile.office_phone,
          email: profile.email,
          website: profile.website,
          linkedin: profile.linkedin,
          instagram: profile.instagram,
          whatsapp: profile.whatsapp,
          youtube_channel: profile.youtube_channel,
          youtube_video: profile.youtube_video,
          facebook: profile.facebook,
          discord: profile.discord,
          twitter: profile.twitter,
          tiktok: profile.tiktok,
          github: profile.github
        })
      });

      if (response.ok) {
        toast.success(t('owner.profileSaved'));
      } else {
        const error = await response.json();
        toast.error(error.detail || t('errors.saveFailed'));
      }
    } catch (error) {
      toast.error(t('errors.serverError'));
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error(t('errors.invalidImageType'));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('errors.imageTooLarge'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setCropImage(reader.result);
      setCropType('photo');
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleCropDone = async (blob) => {
    setCropImage(null);
    if (cropType === 'photo') {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', blob, 'photo.jpg');
      try {
        const response = await fetch(`${API}/owner/profiles/${profileId}/photo`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        if (response.ok) {
          const data = await response.json();
          setProfile(prev => ({ ...prev, photo: data.photo }));
          toast.success(t('owner.photoUploaded'));
        } else {
          toast.error(t('errors.uploadFailed'));
        }
      } catch {
        toast.error(t('errors.serverError'));
      } finally {
        setUploading(false);
      }
    } else if (cropType === 'banner') {
      setUploadingBanner(true);
      const formData = new FormData();
      formData.append('banner_type', 'image');
      formData.append('file', blob, 'banner.jpg');
      try {
        const response = await fetch(`${API}/owner/profiles/${profileId}/banner`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        if (response.ok) {
          const data = await response.json();
          setProfile(prev => ({ ...prev, banner: data.banner }));
          toast.success(t('owner.bannerUpdated') || 'Banniere mise a jour');
        } else {
          toast.error(t('errors.uploadFailed'));
        }
      } catch {
        toast.error(t('errors.serverError'));
      } finally {
        setUploadingBanner(false);
      }
    }
    setCropType(null);
  };

  const handleBannerColorSelect = async (color) => {
    setUploadingBanner(true);
    try {
      const formData = new FormData();
      formData.append('banner_type', 'color');
      formData.append('banner_color', color);

      const response = await fetch(`${API}/owner/profiles/${profileId}/banner`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setProfile(prev => ({ ...prev, banner: data.banner }));
        toast.success(t('owner.bannerUpdated') || 'Banniere mise a jour');
      } else {
        toast.error(t('errors.saveFailed'));
      }
    } catch (error) {
      toast.error(t('errors.serverError'));
    } finally {
      setUploadingBanner(false);
    }
  };

  const handleBannerImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error(t('errors.invalidImageType') || 'Format invalide');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('errors.imageTooLarge') || 'Image trop grande (max 5MB)');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setCropImage(reader.result);
      setCropType('banner');
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleRemoveBanner = async () => {
    setUploadingBanner(true);
    try {
      const response = await fetch(`${API}/owner/profiles/${profileId}/banner`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
        
      });
      if (response.ok) {
        setProfile(prev => ({ ...prev, banner: null }));
        toast.success('Banniere supprimee');
      }
    } catch (error) {
      toast.error(t('errors.serverError'));
    } finally {
      setUploadingBanner(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <p className="text-slate-500">{t('errors.profileNotFound')}</p>
      </div>
    );
  }

  const photoUrl = profile.photo?.startsWith('/api') 
    ? `${process.env.REACT_APP_BACKEND_URL}${profile.photo}` 
    : profile.photo;

  return (
    <div className="min-h-screen pt-24 pb-16 bg-slate-50">
      {cropImage && (
        <ImageCropper
          image={cropImage}
          cropShape={cropType === 'photo' ? 'round' : 'rect'}
          aspect={cropType === 'photo' ? 1 : 16 / 9}
          onCropDone={handleCropDone}
          onCancel={() => { setCropImage(null); setCropType(null); }}
        />
      )}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-4 mb-4">
            <Link to="/owner">
              <Button variant="ghost" size="sm" className="gap-2">
                <ArrowLeft className="w-4 h-4" />
                {t('common.back')}
              </Button>
            </Link>
          </div>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">
                {t('owner.editProfile')}
              </h1>
              <p className="text-slate-600 font-mono">
                {profile.profile_id}
              </p>
            </div>
            <a 
              href={`/user/${profile.profile_id}`} 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <Button variant="outline" className="rounded-xl gap-2">
                <ExternalLink className="w-4 h-4" />
                {t('owner.viewPublicProfile')}
              </Button>
            </a>
          </div>
        </motion.div>

        <form onSubmit={handleSubmit}>
          {/* Photo Section */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl shadow-soft p-6 mb-6"
          >
            <h2 className="font-heading text-lg font-semibold text-slate-900 mb-4">
              {t('owner.profilePhoto')}
            </h2>
            <div className="flex items-center gap-6">
              {photoUrl ? (
                <img 
                  src={photoUrl} 
                  alt="" 
                  className="w-24 h-24 rounded-full object-cover"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-slate-200 flex items-center justify-center">
                  <User className="w-10 h-10 text-slate-400" />
                </div>
              )}
              <div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                  id="photo-upload"
                  disabled={uploading}
                />
                <label htmlFor="photo-upload">
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="rounded-xl gap-2 cursor-pointer"
                    disabled={uploading}
                    asChild
                  >
                    <span>
                      <Upload className="w-4 h-4" />
                      {uploading ? t('common.uploading') : t('owner.uploadPhoto')}
                    </span>
                  </Button>
                </label>
                <p className="text-xs text-slate-500 mt-2">
                  {t('owner.photoRequirements')}
                </p>
              </div>
            </div>
          </motion.div>

          {/* Banner Customization */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-white rounded-2xl shadow-soft p-6 mb-6"
          >
            <h2 className="font-heading text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Palette className="w-5 h-5" />
              {t('owner.profileBanner') || 'Banniere du profil'}
            </h2>

            {/* Banner Preview */}
            <div className="mb-4 rounded-xl overflow-hidden h-24 relative" data-testid="banner-preview">
              {profile.banner?.type === 'image' ? (
                <img
                  src={profile.banner.value.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${profile.banner.value}` : profile.banner.value}
                  alt="Banner"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div
                  className="w-full h-full"
                  style={{ backgroundColor: profile.banner?.value || '#1e293b' }}
                />
              )}
              {profile.banner && (
                <button
                  type="button"
                  onClick={handleRemoveBanner}
                  className="absolute top-2 right-2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1 transition-colors"
                  data-testid="remove-banner-btn"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Color Selector */}
            <p className="text-sm text-slate-600 mb-2">{t('owner.selectColor') || 'Couleur de fond'}</p>
            <div className="flex flex-wrap gap-2 mb-4" data-testid="banner-color-selector">
              {BANNER_COLORS.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => handleBannerColorSelect(c.value)}
                  disabled={uploadingBanner}
                  className={`w-10 h-10 rounded-lg border-2 transition-all hover:scale-110 ${
                    profile.banner?.type === 'color' && profile.banner?.value === c.value
                      ? 'border-blue-500 ring-2 ring-blue-200'
                      : 'border-transparent'
                  }`}
                  style={{ backgroundColor: c.value }}
                  title={c.label}
                  data-testid={`banner-color-${c.value.slice(1)}`}
                />
              ))}
            </div>

            {/* Image Upload */}
            <p className="text-sm text-slate-600 mb-2">{t('owner.uploadBannerImage') || 'Ou uploader une image'}</p>
            <div className="flex items-center gap-3">
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleBannerImageUpload}
                className="hidden"
                id="banner-upload"
                disabled={uploadingBanner}
              />
              <label htmlFor="banner-upload">
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-xl gap-2 cursor-pointer"
                  disabled={uploadingBanner}
                  asChild
                >
                  <span>
                    <ImageIcon className="w-4 h-4" />
                    {uploadingBanner ? t('common.uploading') || 'Upload...' : t('owner.uploadImage') || 'Choisir une image'}
                  </span>
                </Button>
              </label>
              <p className="text-xs text-slate-500">JPG, PNG (max 5MB)</p>
            </div>
          </motion.div>

          {/* Basic Info */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl shadow-soft p-6 mb-6"
          >
            <h2 className="font-heading text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <User className="w-5 h-5" />
              {t('owner.basicInfo')}
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>{t('owner.firstName')}</Label>
                <Input
                  value={profile.first_name || ''}
                  onChange={(e) => handleChange('first_name', e.target.value)}
                  className="rounded-xl mt-1"
                  data-testid="input-first-name"
                />
              </div>
              <div>
                <Label>{t('owner.lastName')}</Label>
                <Input
                  value={profile.last_name || ''}
                  onChange={(e) => handleChange('last_name', e.target.value)}
                  className="rounded-xl mt-1"
                  data-testid="input-last-name"
                />
              </div>
              <div>
                <Label>{t('owner.jobTitle')}</Label>
                <Input
                  value={profile.job_title || ''}
                  onChange={(e) => handleChange('job_title', e.target.value)}
                  className="rounded-xl mt-1"
                  placeholder="Designer, Developer..."
                  data-testid="input-job-title"
                />
              </div>
              <div>
                <Label>{t('owner.company')}</Label>
                <Input
                  value={profile.company || ''}
                  onChange={(e) => handleChange('company', e.target.value)}
                  className="rounded-xl mt-1"
                  data-testid="input-company"
                />
              </div>
              <div className="md:col-span-2">
                <Label>{t('owner.bio')}</Label>
                <textarea
                  value={profile.bio || ''}
                  onChange={(e) => handleChange('bio', e.target.value)}
                  className="w-full rounded-xl mt-1 p-3 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
                  rows={3}
                  placeholder={t('owner.bioPlaceholder')}
                  data-testid="input-bio"
                />
              </div>
            </div>
          </motion.div>

          {/* Contact Info */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl shadow-soft p-6 mb-6"
          >
            <h2 className="font-heading text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Phone className="w-5 h-5" />
              {t('owner.contactInfo')}
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  {t('owner.phone')}
                </Label>
                <Input
                  value={profile.phone || ''}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="rounded-xl mt-1"
                  placeholder="+33 6 12 34 56 78"
                  data-testid="input-phone"
                />
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  {t('owner.officePhone')}
                </Label>
                <Input
                  value={profile.office_phone || ''}
                  onChange={(e) => handleChange('office_phone', e.target.value)}
                  className="rounded-xl mt-1"
                  placeholder="+33 1 23 45 67 89"
                  data-testid="input-office-phone"
                />
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  {t('owner.email')}
                </Label>
                <Input
                  type="email"
                  value={profile.email || ''}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="rounded-xl mt-1"
                  data-testid="input-email"
                />
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  {t('owner.website')}
                </Label>
                <Input
                  value={profile.website || ''}
                  onChange={(e) => handleChange('website', e.target.value)}
                  className="rounded-xl mt-1"
                  placeholder="https://example.com"
                  data-testid="input-website"
                />
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  <MessageCircle className="w-4 h-4" />
                  WhatsApp
                </Label>
                <Input
                  value={profile.whatsapp || ''}
                  onChange={(e) => handleChange('whatsapp', e.target.value)}
                  className="rounded-xl mt-1"
                  placeholder="+33612345678"
                  data-testid="input-whatsapp"
                />
              </div>
            </div>
          </motion.div>

          {/* Social Links */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-2xl shadow-soft p-6 mb-6"
          >
            <h2 className="font-heading text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Hash className="w-5 h-5" />
              {t('owner.socialLinks')}
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="flex items-center gap-2">
                  <Linkedin className="w-4 h-4" />
                  LinkedIn
                </Label>
                <Input
                  value={profile.linkedin || ''}
                  onChange={(e) => handleChange('linkedin', e.target.value)}
                  className="rounded-xl mt-1"
                  placeholder="https://linkedin.com/in/username"
                  data-testid="input-linkedin"
                />
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  <Instagram className="w-4 h-4" />
                  Instagram
                </Label>
                <Input
                  value={profile.instagram || ''}
                  onChange={(e) => handleChange('instagram', e.target.value)}
                  className="rounded-xl mt-1"
                  placeholder="@username"
                  data-testid="input-instagram"
                />
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  <Twitter className="w-4 h-4" />
                  Twitter / X
                </Label>
                <Input
                  value={profile.twitter || ''}
                  onChange={(e) => handleChange('twitter', e.target.value)}
                  className="rounded-xl mt-1"
                  placeholder="@username"
                  data-testid="input-twitter"
                />
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  <Facebook className="w-4 h-4" />
                  Facebook
                </Label>
                <Input
                  value={profile.facebook || ''}
                  onChange={(e) => handleChange('facebook', e.target.value)}
                  className="rounded-xl mt-1"
                  placeholder="https://facebook.com/username"
                  data-testid="input-facebook"
                />
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  <Github className="w-4 h-4" />
                  GitHub
                </Label>
                <Input
                  value={profile.github || ''}
                  onChange={(e) => handleChange('github', e.target.value)}
                  className="rounded-xl mt-1"
                  placeholder="https://github.com/username"
                  data-testid="input-github"
                />
              </div>
              <div>
                <Label>Discord</Label>
                <Input
                  value={profile.discord || ''}
                  onChange={(e) => handleChange('discord', e.target.value)}
                  className="rounded-xl mt-1"
                  placeholder="username#0001"
                  data-testid="input-discord"
                />
              </div>
              <div>
                <Label>TikTok</Label>
                <Input
                  value={profile.tiktok || ''}
                  onChange={(e) => handleChange('tiktok', e.target.value)}
                  className="rounded-xl mt-1"
                  placeholder="@username"
                  data-testid="input-tiktok"
                />
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  <Youtube className="w-4 h-4" />
                  {t('owner.youtubeChannel')}
                </Label>
                <Input
                  value={profile.youtube_channel || ''}
                  onChange={(e) => handleChange('youtube_channel', e.target.value)}
                  className="rounded-xl mt-1"
                  placeholder="https://youtube.com/@channel"
                  data-testid="input-youtube-channel"
                />
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  <Youtube className="w-4 h-4" />
                  {t('owner.youtubeVideo')}
                </Label>
                <Input
                  value={profile.youtube_video || ''}
                  onChange={(e) => handleChange('youtube_video', e.target.value)}
                  className="rounded-xl mt-1"
                  placeholder="https://youtube.com/watch?v=..."
                  data-testid="input-youtube-video"
                />
              </div>
            </div>
          </motion.div>

          {/* Submit */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="flex justify-end gap-4"
          >
            <Link to="/owner">
              <Button type="button" variant="outline" className="rounded-xl">
                {t('common.cancel')}
              </Button>
            </Link>
            <Button 
              type="submit" 
              className="btn-primary rounded-xl gap-2"
              disabled={saving}
              data-testid="save-profile-btn"
            >
              <Save className="w-4 h-4" />
              {saving ? t('common.saving') : t('common.save')}
            </Button>
          </motion.div>
        </form>
      </div>
    </div>
  );
}
